import React, { useId } from 'react';
import { useStore } from '@tanstack/react-form';
import { useFieldContext } from '.';
import { FieldErrors } from './FieldErrors';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { Theme } from '@mui/material';
import { TiptapEditor, useTiptap } from '../../containers/NewsEntryForm/Tiptap';

type TipTapFieldProps = {
  label: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
};

export const TipTapField = (props: TipTapFieldProps) => {
  const field = useFieldContext<string>();
  const hasTriedToSubmit = useStore(
    field.form.store,
    (state) => state.submissionAttempts > 0
  );

  const labelId = useId();
  const editor = useTiptap({
    content: field.state.value || '',
    onUpdate({ editor: updatedEditor }) {
      const html = updatedEditor.getHTML();
      // Only update if the content actually changed to avoid infinite loops
      if (html !== field.state.value) {
        field.handleChange(html);
      }
    },
    onBlur() {
      field.handleBlur();
    },
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-labelledby': labelId,
        'aria-describedby': `${labelId}-helper`,
        'aria-invalid': field.state.meta.errors.length > 0 ? 'true' : 'false',
      },
    },
    editable: !props.disabled,
  });

  const hasError =
    (field.state.meta.isTouched || hasTriedToSubmit) &&
    field.state.meta.errors.length > 0;

  return (
    <FormControl
      component="fieldset"
      variant="outlined"
      error={hasError}
      disabled={props.disabled}
      fullWidth
    >
      <FormLabel component="legend" id={labelId}>
        {props.label}
      </FormLabel>
      <TiptapEditor
        editor={editor}
        styles={{
          outlineOffset: '-1px',
          outline: '1px solid',
          outlineColor: (theme: Theme) =>
            hasError ? theme.palette.error.main : theme.palette.grey[400],
          borderRadius: (theme: Theme) => `${theme.shape.borderRadius}px`,
          boxSizing: 'content-box',
          minHeight: props.minHeight || 120,
          ':hover': {
            outlineColor: (theme: Theme) =>
              hasError ? theme.palette.error.main : theme.palette.grey[800],
          },
          ':focus-within': {
            outline: '2px solid',
            outlineColor: (theme: Theme) =>
              hasError ? theme.palette.error.main : theme.palette.primary.main,
          },
          '[contenteditable=true]': {
            p: 2,
            outline: 'none',
            minHeight: props.minHeight ? props.minHeight - 40 : 80,
            '&:empty:before': {
              content: `"${props.placeholder || ''}"`,
              color: (theme: Theme) => theme.palette.text.disabled,
              pointerEvents: 'none',
            },
          },
          'img.ProseMirror-selectednode': {
            outline: '2px solid',
            outlineColor: (theme: Theme) => theme.palette.primary.main,
          },
        }}
      />
      <FormHelperText id={`${labelId}-helper`} error={hasError}>
        <FieldErrors meta={field.state.meta} />
      </FormHelperText>
    </FormControl>
  );
};
